import { useContext, useEffect } from 'react';
import get from 'lodash/get';
import classnames from 'classnames';
import ApplicationContext from 'components/Application/Context';
import { PosthogActionPhases } from 'components/PostHog/PosthogEvents';
import { getReportingWindow } from './utils';
import Footer from './Footer/Footer';
import './View.scss';
import { showDisplayFooter, trackPosthog } from './SectionUtils';
import { PlacementProps, RenderLabelProps, ShouldRenderType } from '../types';
import { FirstColumn } from './MainSections/FirstColumn';
import { QualityStatsAttrSection } from './MainSections/QualityStatsAttrSection';
import ViewershipTable from '../../Viewership/ViewershipTable/ViewershipTable';

const cbn = 'placement-view';

type PlacementViewProps = {
  placement: PlacementProps;
  showHeaderAndFooter?: boolean | null;
  showGalleryThumbs?: boolean | null;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  filters?: any;
  shouldRender: ShouldRenderType;
  renderLabel?: RenderLabelProps;
  search?: string | null;
};

function PlacementView({
  placement,
  showHeaderAndFooter = false,
  showGalleryThumbs = true,
  filters = null,
  shouldRender = (section) => section !== 'performanceSection',
  renderLabel = (field, defaultLabel) => defaultLabel,
  search = undefined,
}: Readonly<PlacementViewProps>) {
  const isPortrait = get(filters, 'f.orientation') === 'portrait';
  const isPresentation = get(filters, 'f.presentation');
  const { user } = useContext(ApplicationContext);

  useEffect(() => {
    trackPosthog(PosthogActionPhases.Load, placement.id, user);
  }, [placement, user]);

  const { brand, campaign, integration, source } = placement;
  const { channel } = source?.production || integration?.production || {};

  const {
    client,
    dateRangeFrom,
    dateRangeTo,
    id: campaignId,
    projectName: campaignName,
    type,
  } = campaign || {};

  const reportingWindow = getReportingWindow(filters, channel, integration);

  const shouldDisplayFooterView = showDisplayFooter(
    shouldRender,
    showHeaderAndFooter ?? false,
  );

  return (
    <div
      className={classnames(cbn, {
        [`${cbn}--portrait`]: isPortrait,
        [`${cbn}--presentation`]: isPresentation,
      })}
    >
      <style>
        {`
        @page {
          size: ${isPortrait ? '21.6cm 29.7cm' : '33.87cm 19.05cm'};
          margin: 0;
          position: relative;
        }
      `}
      </style>
      <FirstColumn
        cbn={cbn}
        showHeaderAndFooter={showHeaderAndFooter}
        placement={placement}
        shouldRender={shouldRender}
        renderLabel={renderLabel}
        search={search}
        showGalleryThumbs={showGalleryThumbs}
      />
      <div className={`${cbn}__second`}>
        <QualityStatsAttrSection
          cbn={cbn}
          shouldRender={shouldRender}
          placement={placement}
          reportingWindow={reportingWindow}
          renderLabel={renderLabel}
        />

        <ViewershipTable
          watchSourceId={source?.watchId}
          benSourceId={source?.id}
          placementId={Number(placement.id)}
        />
      </div>

      {shouldDisplayFooterView && (
        <Footer
          shouldRender={shouldRender}
          brand={brand}
          campaignId={campaignId}
          campaignName={campaignName}
          campaignType={type}
          dateRangeFrom={dateRangeFrom}
          dateRangeTo={dateRangeTo}
          client={client}
        />
      )}
    </div>
  );
}

export default PlacementView;
